import { useEffect, useRef, type MutableRefObject } from 'react'
import { Box } from '@mui/material'
import { FA_COLOR, type EmotionKey } from './constants'
import { HISTORY_SECONDS, type LiveSample } from './useLiveEmotions'

/** Stacked from the bottom; neutral is left out — it's whatever the rest isn't. */
const STACK: EmotionKey[] = ['happy', 'surprised', 'sad', 'angry', 'disgusted', 'fearful']
/** Absolute scale: a faint expression stays a thin band instead of filling the chart. */
const FULL_SCALE = 0.85

/**
 * Last HISTORY_SECONDS of non-neutral emotions as a stacked stream. Redrawn
 * every animation frame against the clock, so it glides left between the
 * ~8 analysed frames per second instead of stepping.
 */
export default function EmotionStream({
  historyRef,
  active,
  top,
  height = 120,
}: {
  historyRef: MutableRefObject<LiveSample[]>
  active: boolean
  top: EmotionKey | null
  height?: number
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const topRef = useRef(top)
  topRef.current = top

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    let raf = 0
    const draw = () => {
      const d = window.devicePixelRatio || 1
      const w = canvas.clientWidth
      const h = canvas.clientHeight
      if (canvas.width !== Math.round(w * d) || canvas.height !== Math.round(h * d)) {
        canvas.width = Math.round(w * d)
        canvas.height = Math.round(h * d)
      }
      const ctx = canvas.getContext('2d')
      if (!ctx) return
      ctx.setTransform(d, 0, 0, d, 0, 0)
      ctx.clearRect(0, 0, w, h)

      // Quiet baseline grid: 25 / 50 / 75 %.
      ctx.strokeStyle = 'rgba(255,255,255,0.06)'
      ctx.lineWidth = 1
      for (const f of [0.25, 0.5, 0.75]) {
        const y = Math.round(h - (f / FULL_SCALE) * h) + 0.5
        ctx.beginPath()
        ctx.moveTo(0, y)
        ctx.lineTo(w, y)
        ctx.stroke()
      }

      const now = performance.now()
      const span = HISTORY_SECONDS * 1000
      const pts = historyRef.current.filter((s) => now - s.t <= span + 500)
      if (pts.length >= 2) {
        const xs = pts.map((s) => w - ((now - s.t) / span) * w)
        let base = pts.map(() => h)
        for (const k of STACK) {
          const next = pts.map((s, i) => base[i] - ((s.e?.[k] ?? 0) / FULL_SCALE) * h)
          ctx.beginPath()
          next.forEach((y, i) => (i ? ctx.lineTo(xs[i], y) : ctx.moveTo(xs[i], y)))
          for (let i = pts.length - 1; i >= 0; i--) ctx.lineTo(xs[i], base[i])
          ctx.closePath()
          ctx.fillStyle = FA_COLOR[k]
          ctx.globalAlpha = topRef.current === k ? 0.95 : 0.4
          ctx.fill()
          base = next
        }
        ctx.globalAlpha = 1
        // Fade the oldest seconds out on the left edge.
        ctx.globalCompositeOperation = 'destination-out'
        const g = ctx.createLinearGradient(0, 0, w * 0.18, 0)
        g.addColorStop(0, 'rgba(0,0,0,1)')
        g.addColorStop(1, 'rgba(0,0,0,0)')
        ctx.fillStyle = g
        ctx.fillRect(0, 0, w * 0.18, h)
        ctx.globalCompositeOperation = 'source-over'
      }
      if (active) raf = requestAnimationFrame(draw)
    }
    draw()
    return () => cancelAnimationFrame(raf)
  }, [active, historyRef])

  return (
    <Box
      component="canvas"
      ref={canvasRef}
      aria-hidden
      sx={{ display: 'block', width: '100%', height, opacity: active ? 1 : 0.25, transition: 'opacity .6s ease' }}
    />
  )
}
